import React, { useState, useRef, useEffect } from "react";
import { LEARNING_MODES, EXAM_TARGETS } from "../../constants/learningModes";

// Icon + nhãn nhóm mục tiêu theo nhóm mode.
const TARGET_META = {
    math: { icon: "🎓", title: "Mục tiêu kỳ thi", heading: "Mục tiêu" },
    programming: { icon: "💻", title: "Ngôn ngữ / công nghệ", heading: "Ngôn ngữ" },
    research: { icon: "🔬", title: "Lĩnh vực nghiên cứu", heading: "Lĩnh vực" },
};

/**
 * PimathToolbar — Thanh công cụ PIMATH hiển thị phía trên input.
 * Chứa: Learning Mode selector + Target selector (đổi theo nhóm mode).
 */
function PimathToolbar({
    learningMode,
    onLearningModeChange,
    examTarget,
    onExamTargetChange,
    targets,
    modeGroup = "math",
}) {
    const [modeOpen, setModeOpen] = useState(false);
    const [targetOpen, setTargetOpen] = useState(false);
    const modeRef = useRef(null);
    const targetRef = useRef(null);

    // Close dropdowns on outside click
    useEffect(() => {
        if (!modeOpen && !targetOpen) return;
        const handler = (e) => {
            if (modeOpen && modeRef.current && !modeRef.current.contains(e.target)) {
                setModeOpen(false);
            }
            if (targetOpen && targetRef.current && !targetRef.current.contains(e.target)) {
                setTargetOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [modeOpen, targetOpen]);

    const targetList = targets && targets.length ? targets : EXAM_TARGETS;
    const meta = TARGET_META[modeGroup] || TARGET_META.math;
    const currentMode = LEARNING_MODES.find((m) => m.id === learningMode) || LEARNING_MODES[0];
    const currentTarget =
        targetList.find((t) => t.id === examTarget) || targetList[0];
    // Mục tiêu "mặc định" (phần tử đầu nhóm) → coi như chưa chọn (không highlight).
    const isDefaultTarget = !currentTarget || currentTarget.id === targetList[0]?.id;

    return (
        <div className="flex items-center gap-2 flex-wrap">
            {/* Learning Mode Selector */}
            <div className="relative" ref={modeRef}>
                <button
                    type="button"
                    onClick={() => { setModeOpen((v) => !v); setTargetOpen(false); }}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${learningMode !== "detailed" && learningMode !== "general_chat"
                            ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                            : learningMode === "general_chat"
                                ? "border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                    title="Chế độ học"
                >
                    <span>{currentMode.icon}</span>
                    <span className="hidden sm:inline">{currentMode.shortLabel}</span>
                    <svg className={`w-3 h-3 transition-transform ${modeOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {modeOpen && (
                    <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1 max-h-80 overflow-y-auto"
                        style={{ animation: "pimSlideUp 0.12s ease-out" }}>
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                            Chế độ học
                        </div>
                        {LEARNING_MODES.map((mode) => (
                            <button
                                key={mode.id}
                                type="button"
                                onClick={() => {
                                    onLearningModeChange(mode.id);
                                    setModeOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${learningMode === mode.id ? "bg-red-50 text-red-700 font-medium" : "text-gray-700"
                                    }`}
                            >
                                <span className="text-sm flex-shrink-0">{mode.icon}</span>
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium">{mode.label}</div>
                                    <div className="text-[10px] text-gray-500 truncate">{mode.description}</div>
                                </div>
                                {learningMode === mode.id && (
                                    <svg className="w-3.5 h-3.5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Target Selector — ẩn ở chế độ chat thường */}
            {modeGroup !== "general" && (
            <div className="relative" ref={targetRef}>
                <button
                    type="button"
                    onClick={() => { setTargetOpen((v) => !v); setModeOpen(false); }}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${!isDefaultTarget
                            ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                    title={meta.title}
                >
                    <span>{meta.icon}</span>
                    <span className="hidden sm:inline">{currentTarget?.label}</span>
                    <svg className={`w-3 h-3 transition-transform ${targetOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {targetOpen && (
                    <div className="absolute bottom-full left-0 mb-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1 max-h-80 overflow-y-auto"
                        style={{ animation: "pimSlideUp 0.12s ease-out" }}>
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                            {meta.heading}
                        </div>
                        {targetList.map((target) => (
                            <button
                                key={target.id}
                                type="button"
                                onClick={() => {
                                    onExamTargetChange(target.id);
                                    setTargetOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${examTarget === target.id ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                                    }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium">{target.label}</div>
                                    <div className="text-[10px] text-gray-500">{target.description}</div>
                                </div>
                                {examTarget === target.id && (
                                    <svg className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            )}
        </div>
    );
}

export default PimathToolbar;
